import type { InputSignal } from '@angular/core';
import { Component, computed, input, output, inject, ChangeDetectionStrategy } from '@angular/core';

import { componentOutletConfigFactory } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { PaginationState } from '@core/ngrx-legacy';
import type { SignInConsent } from '@core/tellent-types';
import { DataTableComponent, PaginationComponent } from '@core/user-interface-legacy';

import type { ConsentTableColumn } from '../types';
import { ConsentHistoryColumn } from '../types';
import { ConsentStatusComponent } from './cell-templates/consent-status/consent-status.component';
import { HistoryComponent } from './cell-templates/history/history.component';

@Component({
  selector: 'tlc-approval-history-table',
  imports: [DataTableComponent, PaginationComponent],
  templateUrl: './approval-history-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './approval-history-table.component.less',
})
export class ApprovalHistoryTableComponent {
  private readonly i18n = inject(I18nService);

  public rows: InputSignal<SignInConsent[]> = input.required();
  public pagination: InputSignal<PaginationState> = input.required();

  public pageChange = output<number>();

  public pageCount = computed(() => {
    if (!this.pagination()) return 0;
    const { totalCount, limit } = this.pagination();
    return !!totalCount && !!limit ? Math.ceil(totalCount / limit) : 0;
  });

  public columns: ConsentTableColumn[] = [
    {
      id: ConsentHistoryColumn.History,
      label: this.i18n.translate(`tlc.current_user.grant_access.table.approval_history`),
      visible: true,
      geometry: {
        width: 'auto',
      },
      options: {
        draggable: false,
        resizable: false,
        removable: false,
        sortable: false,
      },
      componentFactory: ({ row }) =>
        componentOutletConfigFactory(HistoryComponent, {
          inputs: {
            row: row,
          },
        }),
    },

    {
      id: ConsentHistoryColumn.Status,
      label: this.i18n.translate(`tlc.current_user.grant_access.table.status`),
      visible: true,
      geometry: {
        width: 'auto',
      },
      options: {
        draggable: false,
        resizable: false,
        removable: false,
        sortable: false,
      },
      componentFactory: ({ row }) =>
        componentOutletConfigFactory(ConsentStatusComponent, {
          inputs: {
            row: row,
          },
        }),
    },
  ];
}
